import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  WeeklyHoursService,
  HoursSubmissionDetail,
  HoursSubmissionRow
} from '../../../services/weekly-hours.service';
import { FlagService } from '../../../shared/services/flag.service';
import { FlagType } from '../../../shared/utils/enums';

/**
 * E4 (client clinique — P5) : dialog de détail d'une soumission de relevé d'heures.
 * Grille jour par jour (proposé vs origine), édition agence, VALIDER / rejeter (motif).
 */
@Component({
  selector: 'app-hours-submission-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule],
  template: `
    <div class="hsd">
      <div class="hsd-header" mat-dialog-title>
        <div class="hsd-header-text">
          <h2>Relevé d'heures — {{ detail?.missionRef || 'Mission' }}</h2>
          @if (detail) {
            <div class="hsd-meta">
              <span class="meta-chip">
                <svg viewBox="0 0 16 16" fill="currentColor" class="ico"><path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM4 9h8l1 6H3l1-6Z"/></svg>
                {{ detail.candidatFullname || '—' }}
              </span>
              @if (detail.startDate) {
                <span class="meta-chip">
                  <svg viewBox="0 0 16 16" fill="currentColor" class="ico"><path d="M4 .5a.5.5 0 0 0-1 0V1H2a2 2 0 0 0-2 2v1h16V3a2 2 0 0 0-2-2h-1V.5a.5.5 0 0 0-1 0V1H4V.5ZM1 5h14v9a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V5Z"/></svg>
                  {{ detail.startDate | date:'dd/MM/yy' }} → {{ detail.endDate | date:'dd/MM/yy' }}
                </span>
              }
              <span class="meta-chip">{{ detail.rows.length }} jour(s)</span>
            </div>
          }
        </div>
        <button class="hsd-close" (click)="close()" aria-label="Fermer" [disabled]="busy">✕</button>
      </div>

      <mat-dialog-content class="hsd-content">
        @if (loading) {
          <div class="hsd-loading">Chargement du relevé...</div>
        } @else if (detail) {
          <div class="hsd-table">
            <div class="hsd-row hsd-head">
              <div>Jour</div>
              <div>Début</div>
              <div>Fin</div>
              <div>Pause (min)</div>
              <div>Valeur d'origine</div>
            </div>
            @for (row of detail.rows; track row.id) {
              <div class="hsd-row hsd-data">
                <div class="hsd-day">{{ row.workingDate | date:'EEE dd/MM/yyyy' }}</div>
                <div>
                  <input type="time" [(ngModel)]="row.actualStartTime" [disabled]="busy" />
                </div>
                <div>
                  <input type="time" [(ngModel)]="row.actualEndTime" [disabled]="busy" />
                </div>
                <div>
                  <!-- Pause réelle affichée telle quelle : le back renvoie la pause prévue si le pro n'a rien saisi -->
                  <input type="number" class="hsd-pause" [(ngModel)]="row.actualPauseMinutes" [disabled]="busy" min="0" max="480" step="5" />
                </div>
                <div class="hsd-original">
                  @if (row.originalStartTime || row.originalEndTime || row.originalPauseMinutes != null) {
                    <span class="orig-time">{{ row.originalStartTime || '—' }} → {{ row.originalEndTime || '—' }}</span>
                    @if (row.originalPauseMinutes != null) {
                      <span class="orig-pause">pause : {{ row.originalPauseMinutes }} min</span>
                    }
                  } @else {
                    —
                  }
                </div>
              </div>
            }
          </div>

          <div class="hsd-reject">
            <label>Motif de rejet <small>(optionnel)</small></label>
            <input type="text" [(ngModel)]="motif" placeholder="Ex. : pause non conforme au planning" [disabled]="busy" />
          </div>
        }
      </mat-dialog-content>

      <mat-dialog-actions align="end" class="hsd-actions">
        <button class="btn btn-ghost" (click)="close()" [disabled]="busy">Fermer</button>
        <button class="btn btn-secondary" (click)="save()" [disabled]="busy || loading">
          {{ busy ? 'Enregistrement…' : 'Enregistrer les modifications' }}
        </button>
        <button class="btn btn-danger" (click)="reject()" [disabled]="busy || loading">Rejeter</button>
        <button class="btn btn-primary" (click)="validate()" [disabled]="busy || loading">Valider</button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .hsd { display: flex; flex-direction: column; }

    .hsd-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      padding: 20px 24px 0;
    }
    .hsd-header h2 { margin: 0; font-size: 18px; font-weight: 600; color: #111827; }
    .hsd-meta { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
    .meta-chip {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 3px 10px; border-radius: 12px;
      font-size: 12px; font-weight: 500;
      background: #F3F4F6; color: #4B5563;
    }
    .meta-chip .ico { width: 12px; height: 12px; opacity: 0.7; }

    .hsd-close {
      border: none; background: transparent; cursor: pointer;
      font-size: 16px; color: #9CA3AF; padding: 6px; line-height: 1;
      border-radius: 8px;
    }
    .hsd-close:hover { background: #F3F4F6; color: #374151; }

    .hsd-content { padding: 16px 24px 8px; }

    .hsd-loading { padding: 40px; text-align: center; color: #6B7280; font-size: 14px; }

    .hsd-table {
      display: flex; flex-direction: column;
      border: 1px solid #E5E7EB; border-radius: 12px;
      overflow: hidden; background: #fff;
    }
    .hsd-row {
      display: grid;
      grid-template-columns: minmax(150px, 1.3fr) minmax(110px, 0.8fr) minmax(110px, 0.8fr) minmax(95px, 0.7fr) minmax(180px, 1.2fr);
      gap: 10px; align-items: center;
      padding: 10px 14px;
    }
    .hsd-head {
      background: #F9FAFB; border-bottom: 1px solid #E5E7EB;
      font-size: 11px; font-weight: 600; color: #6B7280;
      text-transform: uppercase; letter-spacing: 0.04em;
    }
    .hsd-data { border-bottom: 1px solid #F3F4F6; }
    .hsd-data:hover { background: #FAFBFC; }
    .hsd-data:last-child { border-bottom: none; }

    .hsd-day { font-size: 13px; font-weight: 600; color: #374151; }

    .hsd-row input {
      width: 100%; box-sizing: border-box;
      padding: 7px 10px;
      border: 1px solid #D1D5DB; border-radius: 8px;
      font-size: 13px; font-family: inherit;
      background: #fff; color: #1F2937;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }
    .hsd-row input:focus {
      outline: none; border-color: #2563EB;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
    }
    .hsd-row input:disabled {
      background: #F3F4F6; border-color: #E5E7EB;
      color: #9CA3AF; cursor: not-allowed;
    }

    .hsd-original { font-size: 12px; color: #9CA3AF; line-height: 1.5; }
    .hsd-original .orig-time { display: block; }
    .hsd-original .orig-pause { color: #B0B7C3; }

    .hsd-reject {
      margin-top: 16px;
      display: flex; flex-direction: column; gap: 6px;
    }
    .hsd-reject label { font-size: 13px; font-weight: 500; color: #374151; }
    .hsd-reject label small { color: #9CA3AF; font-weight: 400; }
    .hsd-reject input {
      padding: 10px 12px;
      border: 1px solid #D1D5DB; border-radius: 8px;
      font-size: 13px; font-family: inherit;
    }
    .hsd-reject input:focus { outline: none; border-color: #2563EB; }

    .hsd-actions {
      padding: 16px 24px 20px;
      display: flex; flex-wrap: wrap; gap: 10px; justify-content: flex-end;
    }
    .btn {
      padding: 9px 18px; border-radius: 10px;
      border: none; cursor: pointer;
      font-size: 13px; font-weight: 500;
      transition: background 0.15s ease, opacity 0.15s ease;
      min-height: 40px;
    }
    .btn:disabled { opacity: 0.55; cursor: not-allowed; }
    .btn-ghost { background: transparent; color: #6B7280; }
    .btn-ghost:hover:not(:disabled) { background: #F3F4F6; }
    .btn-secondary { background: #E5E7EB; color: #111827; }
    .btn-secondary:hover:not(:disabled) { background: #D1D5DB; }
    .btn-danger { background: #FEE2E2; color: #991B1B; }
    .btn-danger:hover:not(:disabled) { background: #FECACA; }
    .btn-primary { background: #3F4A78; color: #fff; }
    .btn-primary:hover:not(:disabled) { background: #343D64; }

    @media (max-width: 768px) {
      .hsd-header, .hsd-content, .hsd-actions { padding-left: 16px; padding-right: 16px; }
      .hsd-actions { flex-direction: column-reverse; }
      .hsd-actions .btn { width: 100%; }
      .hsd-row { min-width: 560px; }
      .hsd-table { overflow-x: auto; }
    }
  `]
})
export class HoursSubmissionDialogComponent implements OnInit {
  detail: HoursSubmissionDetail | null = null;
  loading = true;
  busy = false;
  motif = '';

  constructor(
    private service: WeeklyHoursService,
    private dialogRef: MatDialogRef<HoursSubmissionDialogComponent>,
    private flag: FlagService,
    @Inject(MAT_DIALOG_DATA) public data: { missionId: number }
  ) {}

  ngOnInit(): void {
    this.service.getSubmission(this.data.missionId).subscribe({
      next: (d) => {
        // Pause : TOUJOURS un nombre — null (pro n'a rien saisi, pas de pause prévue)
        // s'affiche 0, jamais un champ vide. Cohérent côté pro (collary-web).
        d.rows.forEach(r => { r.actualPauseMinutes = r.actualPauseMinutes ?? 0; });
        this.detail = d;
        this.loading = false;
      },
      error: () => {
        this.flag.displayFlag('Erreur lors du chargement du relevé', FlagType.error);
        this.loading = false;
      }
    });
  }

  /**
   * Écrit chaque ligne du relevé (début/fin/pause éventuellement corrigés par l'agence) puis,
   * quand TOUTES les lignes sont persistées, passe le relevé en VALIDATED.
   * Sans la phase d'écriture, « Valider » envoyait uniquement le POST validate et les
   * corrections saisies dans la grille étaient perdues.
   */
  private saveAllRowsThen(afterAll: () => void): void {
    if (!this.detail) return;
    this.busy = true;
    const rows = this.detail.rows.filter(r => r.actualStartTime && r.actualEndTime);
    if (rows.length === 0) {
      this.busy = false;
      afterAll();
      return;
    }
    let failures = 0;
    let done = 0;
    const step = () => {
      done++;
      if (done < rows.length) return;
      this.busy = false;
      if (failures > 0) {
        this.flag.displayFlag('Erreur lors de l\'enregistrement — validation annulée', FlagType.error);
        return;
      }
      afterAll();
    };
    rows.forEach((row: HoursSubmissionRow) => {
      this.service.editSubmissionRow(this.data.missionId, row.id, {
        actualStartTime: row.actualStartTime || undefined,
        actualEndTime: row.actualEndTime || undefined,
        // Toujours numérique : 0 reste 0.
        actualPauseMinutes: Number(row.actualPauseMinutes) || 0,
        comment: row.comment
      }).subscribe({ next: step, error: () => { failures++; step(); } });
    });
  }

  save(): void {
    this.saveAllRowsThen(() => {
      this.flag.displayFlag('Modifications enregistrées', FlagType.success);
      this.dialogRef.close(true);
    });
  }

  validate(): void {
    // 1) persister les lignes (heures + pause éditées) — 2) valider le relevé.
    this.saveAllRowsThen(() => {
      this.busy = true;
      this.service.validateSubmission(this.data.missionId).subscribe({
        next: () => {
          this.flag.displayFlag('Relevé d\'heures validé', FlagType.success);
          this.busy = false;
          this.dialogRef.close(true);
        },
        error: () => {
          this.flag.displayFlag('Erreur lors de la validation', FlagType.error);
          this.busy = false;
        }
      });
    });
  }

  reject(): void {
    // Idem validation : d'abord persister les corrections (elles sont conservées pour la
    // correction par le pro en DRAFT), puis passer le statut à REJECTED.
    this.saveAllRowsThen(() => {
      this.busy = true;
      this.service.rejectSubmission(this.data.missionId, this.motif).subscribe({
        next: () => {
          this.flag.displayFlag('Relevé d\'heures rejeté', FlagType.success);
          this.busy = false;
          this.dialogRef.close(true);
        },
        error: () => {
          this.flag.displayFlag('Erreur lors du rejet', FlagType.error);
          this.busy = false;
        }
      });
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
